import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { AiImagePreset, RecipeSearchAssetResult } from '../types';
import { getAiImageAssetUrl } from '../utils';

interface RecipeRefreshPanelProps {
  recipePresets: AiImagePreset[];
  selectedRecipe: RecipeSearchAssetResult | null;
  selectedPresetId: string;
  searchResults: RecipeSearchAssetResult[];
  searching: boolean;
  onSearch: (query: string) => Promise<void>;
  onSelectRecipe: (recipe: RecipeSearchAssetResult) => void;
  onPresetChange: (presetId: string) => void;
}

function recipeImageUrl(recipe: RecipeSearchAssetResult): string {
  return getAiImageAssetUrl(recipe.thumbnailUrl || recipe.previewUrl) || '';
}

const RecipeRefreshPanel: React.FC<RecipeRefreshPanelProps> = ({
  recipePresets,
  selectedRecipe,
  selectedPresetId,
  searchResults,
  searching,
  onSearch,
  onSelectRecipe,
  onPresetChange,
}) => {
  const [query, setQuery] = useState('');
  const [resultsOpen, setResultsOpen] = useState(true);

  const handleSearch = async () => {
    if (query.trim().length < 2 || searching) return;
    setResultsOpen(true);
    await onSearch(query.trim());
  };

  const selectRecipe = (recipe: RecipeSearchAssetResult) => {
    onSelectRecipe(recipe);
    setResultsOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Alert severity="info" icon={<RestaurantIcon />}>
        Sök fram en befintlig receptbild. Originalbilden används som referens och den valda AI-bilden kan ersätta asseten som ny version.
      </Alert>

      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          label="Sök recept"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              void handleSearch();
            }
          }}
          placeholder="Receptnamn, ingrediens..."
          fullWidth
        />
        <Button
          variant="contained"
          disabled={query.trim().length < 2 || searching}
          onClick={() => void handleSearch()}
          sx={{ textTransform: 'none', minWidth: 96 }}
        >
          {searching ? <CircularProgress size={20} /> : 'Sök'}
        </Button>
      </Stack>

      {searchResults.length > 0 && resultsOpen && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 320, overflow: 'auto' }}>
          {searchResults.map((recipe) => {
            const selected = selectedRecipe?._id === recipe._id;
            return (
              <Card key={recipe._id} variant="outlined" sx={{ borderRadius: 2, borderColor: selected ? 'primary.main' : 'divider' }}>
                <CardActionArea onClick={() => selectRecipe(recipe)} sx={{ display: 'flex', alignItems: 'stretch', justifyContent: 'flex-start' }}>
                  {recipeImageUrl(recipe) && (
                    <CardMedia
                      component="img"
                      image={recipeImageUrl(recipe)}
                      alt={recipe.recipeName || recipe.title}
                      sx={{ width: 88, height: 88, objectFit: 'cover', flexShrink: 0 }}
                    />
                  )}
                  <CardContent sx={{ py: 1, px: 1.25, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                      {recipe.recipeName || recipe.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {recipe.fileName}
                    </Typography>
                    {recipe.description && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }} noWrap>
                        {recipe.description}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Box>
      )}

      {searchResults.length > 0 && !resultsOpen && (
        <Button size="small" onClick={() => setResultsOpen(true)} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
          Byt recept
        </Button>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          p: selectedRecipe ? 1.25 : 0,
          borderRadius: 2.5,
          border: selectedRecipe ? '1px solid' : 'none',
          borderColor: 'divider',
          bgcolor: selectedRecipe ? 'background.paper' : 'transparent',
        }}
      >
        {selectedRecipe && (
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
            {recipeImageUrl(selectedRecipe) && (
              <Box
                component="img"
                src={recipeImageUrl(selectedRecipe)}
                alt={selectedRecipe.recipeName || selectedRecipe.title}
                sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1.5, flexShrink: 0 }}
              />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Vald receptbild
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }} noWrap>
                {selectedRecipe.recipeName || selectedRecipe.title}
              </Typography>
              {(selectedRecipe.ingredients || selectedRecipe.mealType || selectedRecipe.cuisine) && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                  {selectedRecipe.mealType && <Chip size="small" label={selectedRecipe.mealType} />}
                  {selectedRecipe.cuisine && <Chip size="small" label={selectedRecipe.cuisine} />}
                  {selectedRecipe.ingredients && <Chip size="small" label="Ingredienser finns" />}
                </Stack>
              )}
            </Box>
          </Box>
        )}

        <FormControl size="small" fullWidth>
          <InputLabel id="recipe-prompt-template-label">Receptmall</InputLabel>
          <Select
            labelId="recipe-prompt-template-label"
            value={selectedPresetId || ''}
            label="Receptmall"
            onChange={(event) => onPresetChange(String(event.target.value))}
            sx={{ borderRadius: 2, bgcolor: 'background.paper' }}
          >
            {recipePresets.length === 0 && (
              <MenuItem value="" disabled>
                Inga receptmallar hittades
              </MenuItem>
            )}
            {recipePresets.map((preset) => (
              <MenuItem key={preset._id} value={preset._id}>
                {preset.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default RecipeRefreshPanel;
